/**
 * Shared Meta / Gupshup WhatsApp template validation and payload building.
 * Single source of truth for CreateTemplateForm, CalendarCampaignBuilder, and useRenewalCreate.
 */

import { CALENDAR_LABEL_TO_FIELD_KEY } from './calendarFieldOptions';

export type MetaTemplateButtonInput = {
  type: string;
  text: string;
  url?: string;
  phone_number?: string;
};

export type MetaTemplateButton = {
  type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';
  text: string;
  url?: string;
  phone_number?: string;
};

export type MetaTemplateDraftInput = {
  bodyText: string;
  headerEnabled: boolean;
  headerType: 'TEXT' | 'IMAGE';
  headerText?: string;
  headerImageHandle?: string;
  footerEnabled?: boolean;
  footerText?: string;
  buttonsEnabled?: boolean;
  buttons?: MetaTemplateButtonInput[];
};

export interface MetaTemplateValidationResult {
  valid: boolean;
  message: string;
}

export type BuildMetaTemplateInput = MetaTemplateDraftInput & {
  name: string;
  language: string;
  category: string;
  headerImageUrl?: string;
};

export type MetaTemplateCreatePayload = {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  components: Array<Record<string, unknown>>;
  header_image_url?: string;
};

const MAX_HEADER_VARIABLES = 1;

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Ordered, deduplicated variable names from {{...}} placeholders. */
export function extractTemplateVariables(text: string): string[] {
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  const vars: string[] = [];
  const seen = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const name = match[1].trim();
    if (!seen.has(name)) {
      seen.add(name);
      vars.push(name);
    }
  }
  return vars;
}

export function replaceWithMetaVars(text: string, vars: string[]): string {
  let result = text;
  vars.forEach((variable, index) => {
    const variableNumber = index + 1;
    const escapedVar = escapeRegExp(variable);
    const regex = new RegExp(`{{\\s*${escapedVar}\\s*}}`, 'g');
    result = result.replace(regex, `{{${variableNumber}}}`);
  });
  return result;
}

/** Meta numeric keys (1, 2, …) for placeholders in a template component. */
export function metaNumericKeysFromText(text: string): string[] {
  return extractTemplateVariables(text).map((_, index) => String(index + 1));
}

export type CalendarTemplateDraftInput = {
  bodyText: string;
  headerEnabled?: boolean;
  headerType?: 'TEXT' | 'IMAGE';
  headerText?: string;
  buttonsEnabled?: boolean;
  buttons?: MetaTemplateButtonInput[];
};

export type CalendarMappingBuildResult = {
  messageTemplate: string;
  headerText: string | null;
  columnMapping: Record<string, string>;
  requiredKeys: string[];
  missingKeys: string[];
};

export type SheetTemplateDraftInput = {
  bodyText: string;
};

export type SheetMappingBuildResult = {
  messageTemplate: string;
  columnMapping: Record<string, string>;
  missingKeys: string[];
};

/**
 * Normalize a sheet-based template draft to Meta {{1}} format and build
 * column_mapping keyed by numeric placeholders. Draft var names are sheet
 * column headers (supports spaces, e.g. {{Fecha Cita}} → {"1": "Fecha Cita"}).
 */
export function buildSheetColumnMapping(
  draft: SheetTemplateDraftInput,
): SheetMappingBuildResult {
  const trimmedBodyText = (draft.bodyText || '').trim();
  const bodyVarNames = extractTemplateVariables(trimmedBodyText);
  const messageTemplate = replaceWithMetaVars(trimmedBodyText, bodyVarNames);
  const columnMapping: Record<string, string> = {};
  const missingKeys: string[] = [];

  bodyVarNames.forEach((varName, index) => {
    const trimmed = varName.trim();
    const metaKey = String(index + 1);
    if (!trimmed) {
      missingKeys.push(metaKey);
      return;
    }
    columnMapping[metaKey] = trimmed;
  });

  return {
    messageTemplate,
    columnMapping,
    missingKeys,
  };
}

const CALENDAR_STANDARD_FIELD_DEFAULTS: Record<string, string> = {
  '1': 'start_date',
  '2': 'start_time',
  '3': 'location',
};

/**
 * Infer calendar event field key from a draft template variable name.
 * Named vars (e.g. {{name}}) map to themselves; numeric vars use standard defaults.
 */
export function resolveCalendarDraftVarToFieldKey(draftName: string): string | undefined {
  const lower = draftName.toLowerCase().trim();
  if (CALENDAR_LABEL_TO_FIELD_KEY[lower]) {
    return CALENDAR_LABEL_TO_FIELD_KEY[lower];
  }
  if (!/^\d+$/.test(draftName)) {
    return draftName;
  }
  return CALENDAR_STANDARD_FIELD_DEFAULTS[draftName];
}

/**
 * Normalize a Calendar template draft to Meta {{1}} format and build column_mapping
 * keyed by numeric placeholders. Auto-maps named draft vars; manual overrides optional.
 */
export function buildCalendarColumnMapping(
  draft: CalendarTemplateDraftInput,
  userColumnMapping: Record<string, string> = {},
): CalendarMappingBuildResult {
  const trimmedBodyText = (draft.bodyText || '').trim();
  const bodyVarNames = extractTemplateVariables(trimmedBodyText);
  const messageTemplate = replaceWithMetaVars(trimmedBodyText, bodyVarNames);
  const bodyKeys = bodyVarNames.map((_, index) => String(index + 1));

  let headerText: string | null = null;
  let headerVarNames: string[] = [];
  let headerKeys: string[] = [];
  if (draft.headerEnabled && draft.headerType === 'TEXT' && (draft.headerText || '').trim()) {
    headerVarNames = extractTemplateVariables(draft.headerText || '');
    headerText = replaceWithMetaVars((draft.headerText || '').trim(), headerVarNames);
    headerKeys = headerVarNames.map((_, index) => String(index + 1));
  }

  const buttonKeys: string[] = [];
  if (draft.buttonsEnabled && draft.buttons) {
    for (const btn of draft.buttons) {
      for (const text of [btn.text || '', btn.url || '']) {
        const vars = extractTemplateVariables(text);
        buttonKeys.push(...vars.map((_, index) => String(index + 1)));
      }
    }
  }

  const requiredKeys = Array.from(
    new Set([...bodyKeys, ...headerKeys, ...buttonKeys]),
  ).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const columnMapping: Record<string, string> = {};
  const missingKeys: string[] = [];

  const resolveMapping = (metaKey: string, draftName: string | undefined): string | undefined => {
    const byKey = userColumnMapping[metaKey]?.trim();
    if (byKey) return byKey;
    if (draftName) {
      const byDraft = userColumnMapping[draftName]?.trim();
      if (byDraft) return byDraft;
      return resolveCalendarDraftVarToFieldKey(draftName);
    }
    return undefined;
  };

  for (let index = 0; index < bodyKeys.length; index++) {
    const metaKey = bodyKeys[index];
    const mapped = resolveMapping(metaKey, bodyVarNames[index]);
    if (mapped) {
      columnMapping[metaKey] = mapped;
    } else {
      missingKeys.push(metaKey);
    }
  }

  for (let index = 0; index < headerKeys.length; index++) {
    const metaKey = headerKeys[index];
    const mapped = resolveMapping(metaKey, headerVarNames[index]);
    if (mapped) {
      if (!columnMapping[metaKey]) {
        columnMapping[metaKey] = mapped;
      }
    } else if (!columnMapping[metaKey]) {
      missingKeys.push(metaKey);
    }
  }

  return {
    messageTemplate,
    headerText,
    columnMapping,
    requiredKeys,
    missingKeys: Array.from(new Set(missingKeys)).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
  };
}

/** Extract ordered numeric placeholder keys from normalized Meta template text. */
export function extractNumericPlaceholderKeys(text: string): string[] {
  const allPh = extractTemplateVariables(text);
  const numeric = allPh.filter((ph) => /^\d+$/.test(ph));
  if (numeric.length === 0) return [];
  const unique = Array.from(new Set(numeric));
  return unique.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
}

export function checkStartsWithVariable(text: string): boolean {
  const trimmed = text.trim();
  return /^\{\{[^}]+\}\}/.test(trimmed);
}

export function checkEndsWithVariable(text: string): boolean {
  const trimmed = text.trim();
  return /\{\{[^}]+\}\}[.!?,;:]*$/.test(trimmed);
}

export function checkVariableToWordRatio(text: string): {
  valid: boolean;
  ratio: number;
  variables: number;
  words: number;
} {
  const variableMatches = text.match(/\{\{[^}]+\}\}/g) || [];
  const variableCount = variableMatches.length;
  const textWithoutVariables = text.replace(/\{\{[^}]+\}\}/g, '').trim();
  const words = textWithoutVariables.split(/\s+/).filter((word) => word.length > 0);
  const wordCount = words.length;
  const minWordsPerVariable = 4;
  const ratio = variableCount > 0 ? wordCount / variableCount : Infinity;
  const valid = variableCount === 0 || wordCount >= variableCount * minWordsPerVariable;
  return { valid, ratio, variables: variableCount, words: wordCount };
}

export function countEmojis(text: string): number {
  const emojiRegex = /(\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
  const matches = text.match(emojiRegex);
  return matches ? matches.length : 0;
}

export function isValidPhoneNumber(phone: string): boolean {
  return /^\+\d{10,15}$/.test(phone);
}

export function normalizeMetaTemplateName(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/** Display name for in-campaign auto-created templates: `{campaignName}_plantilla`. */
export function buildCampaignTemplateName(campaignName: string): string {
  return `${(campaignName || "").trim()}_plantilla`;
}

export const DEFAULT_TEMPLATE_TYPE = "Utilidad: Alertas y actualizaciones";
export const DEFAULT_TEMPLATE_LANGUAGE = "Español";

export function resolveMetaLanguageCode(language: string): string {
  const lower = language.toLowerCase();
  if (lower.includes('inglés') || lower.includes('english') || lower === 'en') {
    return 'en_US';
  }
  if (lower === 'pt' || lower.includes('portugu')) {
    return 'pt_BR';
  }
  if (lower === 'es_mx') {
    return 'es_MX';
  }
  return 'es_MX';
}

export function resolveMetaCategory(category: string): 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' {
  const lower = category.toLowerCase();
  if (lower.includes('marketing')) {
    return 'MARKETING';
  }
  if (lower.includes('auth')) {
    return 'AUTHENTICATION';
  }
  return 'UTILITY';
}

export function validateMetaTemplateDraft(input: MetaTemplateDraftInput): MetaTemplateValidationResult {
  const bodyText = input.bodyText || '';

  if (!bodyText.trim()) {
    return { valid: false, message: 'El mensaje de la plantilla no puede estar vacío.' };
  }

  const emojiCount = countEmojis(bodyText);
  if (emojiCount > 8) {
    return {
      valid: false,
      message: 'Meta no permite más de 8 emojis en la plantilla para asegurar la aprobación.',
    };
  }

  const bodyRatio = checkVariableToWordRatio(bodyText);
  if (!bodyRatio.valid) {
    const wordsNeeded = bodyRatio.variables * 4 - bodyRatio.words;
    return {
      valid: false,
      message:
        `⚠️ Meta rechazará esta plantilla: Tienes ${bodyRatio.variables} dato(s) pero solo ${bodyRatio.words} palabra(s) de texto. ` +
        `Necesitas agregar aproximadamente ${wordsNeeded} palabra(s) más para que Meta la apruebe. ` +
        'Tip: Agrega más contexto y detalles al mensaje.',
    };
  }

  if (checkStartsWithVariable(bodyText) || checkEndsWithVariable(bodyText)) {
    const issues: string[] = [];
    if (checkStartsWithVariable(bodyText)) issues.push('comience con una variable');
    if (checkEndsWithVariable(bodyText)) issues.push('termine con una variable (ni siquiera con puntuación después)');
    return {
      valid: false,
      message:
        `Meta no permite que la plantilla ${issues.join(' ni ')}. ` +
        'Agrega texto antes/después de las variables. Ejemplo: "Hola, tu monto es {{Monto}} pesos."',
    };
  }

  if (input.headerEnabled) {
    if (input.headerType === 'IMAGE') {
      if (!input.headerImageHandle?.trim()) {
        return {
          valid: false,
          message: 'Debes subir una imagen para el encabezado antes de continuar.',
        };
      }
    } else if (input.headerType === 'TEXT') {
      const headerText = (input.headerText || '').trim();
      if (!headerText) {
        return {
          valid: false,
          message: 'Escribe el texto del encabezado o desactiva el encabezado.',
        };
      }

      const headerVars = extractTemplateVariables(headerText);
      if (headerVars.length > MAX_HEADER_VARIABLES) {
        return {
          valid: false,
          message:
            'El encabezado solo permite 1 variable. WhatsApp no acepta más de un dato en el header.',
        };
      }

      if (checkStartsWithVariable(headerText) || checkEndsWithVariable(headerText)) {
        const issues: string[] = [];
        if (checkStartsWithVariable(headerText)) issues.push('comience con una variable');
        if (checkEndsWithVariable(headerText)) issues.push('termine con una variable (ni siquiera con puntuación después)');
        return {
          valid: false,
          message:
            `Meta no permite que el encabezado ${issues.join(' ni ')}. ` +
            'Agrega texto antes/después de la variable.',
        };
      }

      const headerRatio = checkVariableToWordRatio(headerText);
      if (!headerRatio.valid) {
        return {
          valid: false,
          message:
            `⚠️ El encabezado tiene demasiados datos (${headerRatio.variables}) para tan poco texto (${headerRatio.words} palabras). ` +
            'Agrega más texto al encabezado o reduce los datos.',
        };
      }
    }
  }

  if (input.buttonsEnabled && input.buttons) {
    for (const btn of input.buttons) {
      if (btn.type === 'PHONE_NUMBER') {
        if (!btn.phone_number || !isValidPhoneNumber(btn.phone_number)) {
          return {
            valid: false,
            message:
              'El número de teléfono en el botón debe incluir el código de país (ej: +57...) y solo contener números, sin espacios ni guiones.',
          };
        }
      }
    }
  }

  return { valid: true, message: '' };
}

export function buildMetaTemplateRequest(input: BuildMetaTemplateInput): MetaTemplateCreatePayload {
  const metaTemplateName = normalizeMetaTemplateName(input.name);
  const trimmedBodyText = (input.bodyText || '').trim();
  const bodyVars = extractTemplateVariables(trimmedBodyText);
  const metaTemplateText = replaceWithMetaVars(trimmedBodyText, bodyVars);
  const exampleValues = bodyVars.map((_, i) => `ejemplo_${i + 1}`);

  const componentsArray: Array<Record<string, unknown>> = [];

  if (input.headerEnabled) {
    if (input.headerType === 'TEXT' && (input.headerText || '').trim()) {
      const headerVars = extractTemplateVariables(input.headerText || '');
      const transformedHeaderText = replaceWithMetaVars((input.headerText || '').trim(), headerVars);
      const headerComponent: Record<string, unknown> = {
        type: 'HEADER',
        format: 'TEXT',
        text: transformedHeaderText,
      };
      if (headerVars.length > 0) {
        headerComponent.example = {
          header_text: headerVars.map((_, i) => `ejemplo_header_${i + 1}`),
        };
      }
      componentsArray.push(headerComponent);
    } else if (input.headerType === 'IMAGE' && input.headerImageHandle) {
      componentsArray.push({
        type: 'HEADER',
        format: 'IMAGE',
        example: {
          header_handle: [input.headerImageHandle],
        },
      });
    }
  }

  const bodyComponent: Record<string, unknown> = {
    type: 'BODY',
    text: metaTemplateText,
  };
  if (bodyVars.length > 0) {
    bodyComponent.example = {
      body_text: [[...exampleValues]],
    };
  }
  componentsArray.push(bodyComponent);

  if (input.footerEnabled && (input.footerText || '').trim()) {
    componentsArray.push({
      type: 'FOOTER',
      text: (input.footerText || '').trim(),
    });
  }

  if (input.buttonsEnabled && input.buttons && input.buttons.length > 0) {
    const validButtons = input.buttons.filter((btn) => btn.text.trim());
    if (validButtons.length > 0) {
      componentsArray.push({
        type: 'BUTTONS',
        buttons: validButtons.map((btn) => {
          const buttonObj: Record<string, unknown> = {
            type: btn.type,
            text: btn.text.trim(),
          };
          if (btn.type === 'URL' && btn.url) {
            let normalizedUrl = btn.url.trim();
            if (normalizedUrl && !normalizedUrl.match(/^https?:\/\//i)) {
              normalizedUrl = 'https://' + normalizedUrl;
            }
            buttonObj.url = normalizedUrl;
            if (normalizedUrl.includes('{{')) {
              buttonObj.example = ['example-value'];
            }
          }
          if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
            buttonObj.phone_number = btn.phone_number.trim();
          }
          return buttonObj;
        }),
      });
    }
  }

  const payload: MetaTemplateCreatePayload = {
    name: metaTemplateName,
    language: resolveMetaLanguageCode(input.language),
    category: resolveMetaCategory(input.category),
    components: componentsArray,
  };

  if (input.headerEnabled && input.headerType === 'IMAGE' && input.headerImageUrl) {
    payload.header_image_url = input.headerImageUrl;
  }

  return payload;
}
